import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import RelatedServicesNav from '../components/RelatedServicesNav';
import StickyMobileCta from '../components/StickyMobileCta';
import {
  Stethoscope, Syringe, Shield, ClipboardCheck, FlaskConical, ScanLine, TestTube, Bandage,
  Scissors, Apple, HeartPulse, Baby, Heart, GraduationCap, Microscope, Wallet, Sparkles, Home as HomeIcon,
  MessageSquare, ClipboardList, Repeat, Dog, Cat, Rabbit, MapPin, Phone, MessageCircle, CalendarDays,
  ArrowRight, Check, Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { CONTACT, GOOGLE_RATING } from '../data/mock';
import { cdnImg } from '../utils/cdn';
import { trackCallClick, trackWhatsAppClick, trackBookButtonClick } from '../lib/analytics';

const ASSET = 'https://customer-assets.emergentagent.com/job_petcare-preview-1/artifacts';
const DR_PORTRAIT = `${ASSET}/jnhxjj9q_WhatsApp%20Image%202026-07-08%20at%2012.31.40.jpeg`;
const HERO_IMAGE = 'https://images.unsplash.com/photo-1583337130417-3346a1be7dee?auto=format&fit=crop&w=1400&q=80';

const TITLE = 'Veterinary Clinic in Noida | Expert Dog & Cat Vet | Ichi & Ori Pet Care';
const DESCRIPTION = 'Looking for a trusted veterinary clinic in Noida? Ichi & Ori Pet Care offers consultations, vaccinations, diagnostics, surgery, pet X-rays, health checkups and compassionate care for dogs, cats and small pets.';
const CANONICAL = 'https://ichiandoripetcare.com/veterinary-clinic-noida';
const set = (sel, attr, val) => { const el = document.head.querySelector(sel); if (el) el.setAttribute(attr, val); };

const REASONS = [
  { Icon: Heart,        title: 'Compassion-First Care',        text: 'Every visit begins with unhurried handling and a genuine effort to make your pet feel safe, not stressed.' },
  { Icon: GraduationCap,title: 'Experienced Veterinarian',     text: 'Dr Kaushal Singh Yadav leads a small, focused team that gets to know your pet across visits.' },
  { Icon: Microscope,   title: 'Modern Diagnostics',           text: 'Digital X-Ray, in-house CBC, ECG and BP monitoring on-site, so most answers arrive during the same visit.' },
  { Icon: ScanLine,     title: 'Digital X-Ray',                text: 'Direct-radiography imaging reviewed on screen within minutes for fractures, chest and abdominal concerns.' },
  { Icon: ClipboardList,title: 'Transparent Treatment Plans',  text: 'Diagnosis, options and honest, upfront costs discussed before any procedure begins.' },
  { Icon: Sparkles,     title: 'Calm, Pet-Friendly Space',     text: 'A clean, quiet clinic in Sector 90 designed around pet comfort, with waiting flow that avoids overwhelm.' },
];

const SERVICE_GROUPS = [
  {
    heading: 'Everyday Veterinary Care',
    items: [
      { Icon: Stethoscope, title: 'General Consultations', text: 'A thorough examination with time to listen to your concerns and explain findings clearly.' },
      { Icon: Syringe,     title: 'Vaccinations',          text: 'WSAVA-guided schedules for puppies, kittens and adults, tailored to lifestyle and history.' },
      { Icon: Shield,      title: 'Preventive Healthcare', text: 'Deworming, tick and flea protection and wellness planning to keep small problems small.' },
      { Icon: ClipboardCheck, title: 'Health Checkups',    text: 'Annual nose-to-tail reviews that catch issues before they become emergencies.' },
    ],
  },
  {
    heading: 'Diagnostics & Investigations',
    items: [
      { Icon: FlaskConical, title: 'In-House Diagnostics', text: 'Faster, better-informed decisions with CBC, ECG, BP and microscopy performed at the clinic.' },
      { Icon: ScanLine,     title: 'Digital X-Ray',         text: 'High-resolution digital radiography for musculoskeletal, chest and abdominal cases.' },
      { Icon: TestTube,     title: 'Laboratory Tests',      text: 'Biochemistry, urine analysis and histopathology processed through accredited partner labs.' },
      { Icon: Bandage,      title: 'Minor Procedures',      text: 'Wound cleaning, suturing, ear flushes and small in-clinic interventions with gentle handling.' },
    ],
  },
  {
    heading: 'Advanced & Long-Term Care',
    items: [
      { Icon: Scissors,   title: 'Surgery',                text: 'Planned procedures including spaying, neutering, tumour removal and wound repair with careful anaesthesia.' },
      { Icon: Apple,      title: 'Nutrition Guidance',     text: 'Honest food and diet advice for weight, life-stage and medical needs, without pushy brand pitches.' },
      { Icon: HeartPulse, title: 'Senior Pet Care',        text: 'Focused support for ageing pets, including screening for kidney, cardiac and mobility issues.' },
      { Icon: Baby,       title: 'Puppy & Kitten Wellness',text: 'Starter plans covering vaccinations, deworming, socialisation advice and nutrition foundations.' },
    ],
  },
];

const JOURNEY = [
  { Icon: CalendarDays, title: 'Appointment',      text: 'Book online, on WhatsApp or by phone. We confirm a time that respects both your schedule and your pet’s.' },
  { Icon: Stethoscope,  title: 'Examination',      text: 'An unhurried, hands-on nose-to-tail exam with your pet given time to settle before we begin.' },
  { Icon: FlaskConical, title: 'Diagnosis',        text: 'In-house tests where useful, so we work from evidence rather than assumption.' },
  { Icon: MessageSquare,title: 'Discussion',       text: 'Findings explained in plain language. You leave the room knowing what is happening and why.' },
  { Icon: ClipboardList,title: 'Treatment Options',text: 'A written plan with expected recovery, alternatives where they exist and honest, upfront costs.' },
  { Icon: Repeat,       title: 'Follow-Up',        text: 'WhatsApp check-ins and easy access to us if anything feels off once your pet is home.' },
];

const CONDITIONS = [
  { Icon: Sparkles,    label: 'Skin allergies & itchiness' },
  { Icon: HeartPulse,  label: 'Vomiting' },
  { Icon: HeartPulse,  label: 'Diarrhea' },
  { Icon: Shield,      label: 'Tick fever' },
  { Icon: Stethoscope, label: 'Ear infections' },
  { Icon: Bandage,     label: 'Limping & lameness' },
  { Icon: Apple,       label: 'Loss of appetite' },
  { Icon: ClipboardCheck, label: 'Routine illness & fevers' },
  { Icon: Syringe,     label: 'Vaccinations & boosters' },
  { Icon: Baby,        label: 'Puppy & kitten care' },
  { Icon: HeartPulse,  label: 'Senior pet health' },
  { Icon: Cat,         label: 'Cats — all life stages' },
  { Icon: Dog,         label: 'Dogs — all breeds & sizes' },
  { Icon: Rabbit,      label: 'Rabbits — routine care' },
];

const LOCATIONS = [
  'Sector 90', 'Sector 137', 'Sector 143', 'Sector 168', 'Sector 93', 'Sector 100',
  'Noida Expressway', 'Paras Tierea', 'ATS Communities', 'Supertech Residences',
];

const FAQS = [
  { q: 'How often should my dog visit a vet?', a: 'A healthy adult dog benefits from an annual wellness check, plus a visit any time behaviour, appetite or bathroom habits change. Puppies and senior dogs need visits more often, typically every 3 to 6 months, so we can update vaccinations and screen for early issues.' },
  { q: 'How often should my cat visit a vet?', a: 'Most healthy adult cats do well with a yearly checkup and vaccination review. Kittens should be seen every few weeks in their first four months, and senior cats benefit from twice-yearly checks, since cats are experts at hiding early signs of illness.' },
  { q: 'What happens during the first consultation at your clinic?', a: 'We start by listening. Once we understand your concern, Dr Kaushal performs a nose-to-tail examination, discusses lifestyle, diet and medical history, and only then recommends tests or treatment. You leave with a written plan, honest cost estimates and a chance to ask everything you need to.' },
  { q: 'Do I need an appointment before visiting?', a: 'Yes. Appointments help us give your pet undivided attention and keep waiting times short. You can book online, on WhatsApp or by calling +91 9560925999. Urgent cases are always accommodated on priority whenever possible.' },
  { q: 'What vaccinations does my pet need?', a: 'Core dog vaccinations include DHPPi-L, Anti-Rabies and Kennel Cough (Bordetella), with Leptospirosis, Canine Coronavirus, Canine Influenza and Lyme added where indicated. Core cat vaccinations include FVRCP and Anti-Rabies, with FeLV added when appropriate. Schedules are tailored to your pet’s age, breed, lifestyle and history.' },
  { q: 'Do you treat senior pets?', a: 'Yes. Senior pets are one of our favourite groups to care for. Our approach is gentler, more diagnostic-led and focused on quality of life, with regular screening for kidney, cardiac, joint and dental issues.' },
  { q: 'Do you perform surgeries?', a: 'Yes. We perform routine and selected surgical procedures including spaying, neutering, tumour removal, wound repair and aural hematoma correction. Every surgery is preceded by a health assessment and clear conversation about recovery and cost.' },
  { q: 'Do you offer X-Ray services for pets?', a: 'Yes. We use a modern Digital DR X-Ray system at the clinic, with images reviewed on screen within minutes for fractures, chest, abdominal and orthopaedic assessments.' },
  { q: 'How can I book an appointment?', a: 'You can book instantly through the Request Appointment button on this page, message us on WhatsApp at +91 9560925999, or call the clinic directly during opening hours from 10:30 AM to 8:30 PM.' },
  { q: 'Where is the veterinary clinic located?', a: 'Ichi & Ori Pet Care is located at G11, Tower C, Noida World One, Sector 90, Noida, Uttar Pradesh 201305. We are conveniently reachable from the Noida Expressway and nearby residential communities in Sectors 90, 93, 100, 137, 143 and 168.' },
];

const INTERNAL_LINKS = [
  { to: '/veterinary',                    label: 'Dog Vet in Noida' },
  { to: '/cat-vet-noida',                 label: 'Cat Vet in Noida' },
  { to: '/pet-doctor-noida',              label: 'Pet Doctor in Noida' },
  { to: '/pet-vaccination-noida',         label: 'Pet Vaccination in Noida' },
  { to: '/pet-surgery-noida',             label: 'Pet Surgery in Noida' },
  { to: '/pet-x-ray-noida',               label: 'Pet X-Ray in Noida' },
  { to: '/pet-diagnostics-noida',         label: 'Pet Diagnostics in Noida' },
  { to: '/home-veterinary-visit-noida',   label: 'Home Vet Visit' },
];

const VeterinaryClinicNoida = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const orig = {
      d: document.head.querySelector('meta[name="description"]')?.getAttribute('content'),
      c: document.head.querySelector('link[rel="canonical"]')?.getAttribute('href'),
    };
    set('meta[name="description"]', 'content', DESCRIPTION);
    set('link[rel="canonical"]', 'href', CANONICAL);
    set('meta[property="og:title"]', 'content', TITLE);
    set('meta[property="og:description"]', 'content', DESCRIPTION);
    set('meta[property="og:url"]', 'content', CANONICAL);
    return () => {
      if (orig.d != null) set('meta[name="description"]', 'content', orig.d);
      if (orig.c != null) set('link[rel="canonical"]', 'href', orig.c);
    };
  }, []);

  const wa = `https://wa.me/${CONTACT.phoneRaw.replace(/\D/g, '')}?text=${encodeURIComponent("Hi, I'd like to book a veterinary consultation at Ichi & Ori Pet Care.")}`;

  const faqSchema = {
    '@context': 'https://schema.org', '@type': 'FAQPage',
    mainEntity: FAQS.map(f => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  };
  const vetSchema = {
    '@context': 'https://schema.org', '@type': 'VeterinaryCare',
    name: 'Ichi and Ori Pet Care, Veterinary Clinic in Noida',
    url: CANONICAL,
    telephone: CONTACT.phoneRaw,
    priceRange: '₹₹',
    address: { '@type': 'PostalAddress', streetAddress: 'G11, Tower C, Noida World One', addressLocality: 'Sector 90, Noida', addressRegion: 'Uttar Pradesh', postalCode: '201305', addressCountry: 'IN' },
    aggregateRating: { '@type': 'AggregateRating', ratingValue: String(GOOGLE_RATING?.rating || 4.9), reviewCount: String(GOOGLE_RATING?.count || 107), bestRating: '5' },
    areaServed: { '@type': 'City', name: 'Noida' },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ichiandoripetcare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Veterinary Clinic in Noida', item: CANONICAL },
    ],
  };

  return (
    <div className="bg-white" data-testid="vet-clinic-noida-page">
      <Helmet>
        <title>{TITLE}</title>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(vetSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* 1 — HERO */}
      <section className="relative bg-gradient-to-br from-[#1a0b3d] via-[#3b1d76] to-[#5b3eb5] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_30%_20%,#e870b6_0%,transparent_40%),radial-gradient(circle_at_80%_70%,#5b3eb5_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 sm:py-1.5 rounded-full bg-[#e870b6]/20 border border-[#e870b6]/40 text-[#ffc1e0] text-[11px] sm:text-xs font-bold tracking-[0.18em] mb-6"><Stethoscope className="w-3.5 h-3.5 flex-shrink-0" />VETERINARY CLINIC · SECTOR 90, NOIDA</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.03] mb-6">Compassionate Veterinary Care for Every Pet in Noida.</h1>
            <p className="text-base sm:text-lg text-white/85 leading-relaxed mb-4 max-w-xl">Ichi &amp; Ori Pet Care is a dedicated veterinary clinic in Sector 90, Noida, offering modern medical care for dogs, cats and small pets, delivered with gentle handling and honest, transparent treatment plans.</p>
            <p className="text-sm sm:text-base text-white/70 leading-relaxed mb-9 max-w-xl">Consultations, vaccinations, diagnostics, digital X-Ray, health checkups and surgery, all under one calm, welcoming roof.</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3 sm:gap-4">
              <Link to="/book" onClick={() => trackBookButtonClick('vet_clinic_noida_hero')} className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
              </Link>
              <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('vet_clinic_noida_hero')} className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] text-white rounded-full px-7 py-6 text-base font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />Chat on WhatsApp</Button>
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('vet_clinic_noida_hero')} className="w-full sm:w-auto sm:ml-1">
                <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                  <Phone className="w-4 h-4" /> Call Now
                </span>
              </a>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl border border-white/15 aspect-[4/5]">
              <img src={HERO_IMAGE} alt="Veterinarian examining a pet at Ichi and Ori Pet Care, Sector 90, Noida — placeholder image" loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <RelatedServicesNav current="/veterinary-clinic-noida" />

      {/* 2 — WHY PET PARENTS CHOOSE */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">WHY PET PARENTS CHOOSE US</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">A veterinary experience built around pets and their families.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REASONS.map((r, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-[#f3d6f5]/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fdf3fc] to-[#f3d6f5] flex items-center justify-center mb-5 flex-shrink-0">
                  <r.Icon className="w-5 h-5 text-[#5b3eb5]" />
                </div>
                <h3 className="font-serif text-[20px] font-bold text-[#1a0b3d] mb-3 leading-snug">{r.title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — COMPREHENSIVE VETERINARY SERVICES */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">COMPREHENSIVE VETERINARY CARE</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Everything your pet needs, under one roof.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">From a first vaccination to a senior wellness plan, our services are designed to keep pets healthy through every stage of life.</p>
          </div>

          <div className="space-y-14">
            {SERVICE_GROUPS.map((g, gi) => (
              <div key={gi}>
                <h3 className="text-xl sm:text-2xl font-bold text-[#1a0b3d] mb-6 flex items-center gap-3">
                  <span className="w-8 h-px bg-[#e870b6]" />
                  {g.heading}
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  {g.items.map((s, si) => (
                    <div key={si} className="bg-[#fdf7fb] rounded-2xl p-6 border border-[#f3d6f5]/60 hover:border-[#e870b6]/50 hover:shadow-md transition-all flex flex-col h-full">
                      <div className="w-11 h-11 rounded-xl bg-white border border-[#f5d3e6] flex items-center justify-center mb-4 flex-shrink-0">
                        <s.Icon className="w-5 h-5 text-[#e870b6]" />
                      </div>
                      <h4 className="font-bold text-[#1a0b3d] text-base mb-2 leading-snug">{s.title}</h4>
                      <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{s.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — MEET OUR VETERINARIAN */}
      <section className="bg-[#1a0b3d] text-white py-20 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_15%_15%,#5b3eb5_0%,transparent_45%),radial-gradient(circle_at_85%_85%,#e870b6_0%,transparent_45%)]" />
        <div className="relative max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-[minmax(260px,380px)_1fr] gap-10 lg:gap-16 items-start">
          <div>
            <div className="rounded-[28px] overflow-hidden shadow-2xl border border-white/15 bg-white/5">
              <img src={cdnImg(DR_PORTRAIT, { w: 760, h: 950, fit: 'cover', a: 'center' })} alt="Dr Kaushal Singh Yadav, Veterinarian at Ichi and Ori Pet Care in Sector 90, Noida" loading="lazy" decoding="async" className="w-full h-auto object-cover" />
            </div>
          </div>
          <div>
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#ffc1e0] mb-4">MEET OUR VETERINARIAN</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08] mb-5">Care led by <span className="text-[#e870b6]">Dr Kaushal Singh Yadav</span>.</h2>
            <p className="text-white/85 text-base sm:text-lg leading-[1.75] max-w-2xl mb-5">Dr Kaushal leads the veterinary team at Ichi &amp; Ori with a compassionate, patient-first approach. He takes time to understand each pet as an individual, explain findings in plain language and recommend treatments based on what your pet actually needs, not what is easiest to sell.</p>
            <p className="text-white/75 text-base sm:text-lg leading-[1.75] max-w-2xl mb-8">If your pet is anxious or uncertain, Dr Kaushal slows down. If you have questions, he answers them, all of them, without hurry. And when something can wait, he says so honestly instead of overprescribing.</p>
            <div className="flex flex-wrap gap-3">
              <Link to="/veterinary" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-sm font-semibold transition-colors">Read more about Dr Kaushal <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* 5 — OUR APPROACH TO VETERINARY CARE */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">OUR APPROACH</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">A calm, transparent journey from question to care.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Every visit follows a simple, honest rhythm. No jargon, no surprises and never a rushed conversation.</p>
          </div>
          <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10">
            {JOURNEY.map((step, i) => (
              <li key={i} className="flex gap-4">
                <div className="flex flex-col items-center flex-shrink-0">
                  <div className="w-11 h-11 rounded-full bg-[#fdf0f8] border border-[#f5d3e6] flex items-center justify-center">
                    <step.Icon className="w-5 h-5 text-[#e870b6]" />
                  </div>
                  <span className="text-[10px] tracking-[0.2em] font-bold text-[#5b3eb5] mt-2">{String(i + 1).padStart(2, '0')}</span>
                </div>
                <div className="pt-1">
                  <h3 className="text-[#1a0b3d] font-bold text-base sm:text-lg mb-1.5 leading-snug">{step.title}</h3>
                  <p className="text-sm text-[#3b1d76]/80 leading-relaxed">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 6 — CONDITIONS WE COMMONLY TREAT */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">CONDITIONS WE COMMONLY TREAT</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">The everyday moments that bring pets to our clinic.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">If something feels off, it is almost always worth a call. Below are some of the concerns we see most often. When you are unsure, ask us first, we are happy to help you decide whether a visit is needed.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {CONDITIONS.map((c, i) => (
              <div key={i} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3.5 border border-[#f3d6f5]/60 shadow-sm">
                <c.Icon className="w-4 h-4 text-[#e870b6] flex-shrink-0" />
                <span className="text-[13px] sm:text-sm font-semibold text-[#1a0b3d] leading-snug">{c.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — WHY EARLY VETERINARY CARE MATTERS */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[860px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-10">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">EARLY CARE MATTERS</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Small habits, big difference.</h2>
          </div>
          <div className="space-y-6 text-[#3b1d76]/85 text-base sm:text-[17px] leading-[1.85]">
            <p>Pets rarely tell us something is wrong in a way we can immediately understand. They hide discomfort, adapt around pain and often continue their routines until the underlying problem becomes hard to reverse. The most powerful thing a pet parent can do is not react faster to illness, it is prevent it altogether.</p>
            <p>An <strong className="text-[#1a0b3d]">annual health checkup</strong> catches small changes before they become emergencies. A subtle weight change, an early dental issue, a slightly abnormal heartbeat, these are the kinds of findings that make routine wellness visits so valuable, especially for puppies, kittens, senior pets and breeds with predictable health patterns.</p>
            <p><strong className="text-[#1a0b3d]">Vaccinations and parasite prevention</strong> remain two of the most cost-effective ways to protect your pet. A well-timed vaccine schedule, along with age-appropriate deworming and tick, flea and mite protection, prevents illnesses that are far harder and more expensive to treat later.</p>
            <p><strong className="text-[#1a0b3d]">Early diagnosis</strong> changes outcomes. Blood work, X-rays and simple in-clinic tests done at the first sign of concern often reveal treatable problems long before symptoms become serious. That is why we invest in in-house diagnostics, so decisions can begin during the same visit rather than at the next appointment.</p>
            <p>If you have never taken your pet for a preventive checkup, this is the sign. Book a visit, meet the team and let us build a plan for the year ahead. It is one of the kindest things you can do as a pet parent.</p>
          </div>
        </div>
      </section>

      {/* 8 — SERVING PET PARENTS ACROSS NOIDA */}
      <section className="bg-[#fdf7fb] py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">SERVING NOIDA</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">A trusted veterinary clinic across Noida.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Pet parents visit us from across the city and the Expressway corridor. If you are nearby, we are easy to reach.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 max-w-3xl mx-auto">
            {LOCATIONS.map((l, i) => (
              <span key={i} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#f3d6f5] text-[#1a0b3d] text-sm font-semibold">
                <MapPin className="w-3.5 h-3.5 text-[#e870b6]" /> {l}
              </span>
            ))}
          </div>
          <div className="text-center">
            <div className="inline-flex items-start gap-3 bg-white rounded-2xl px-6 py-4 border border-[#f3d6f5] shadow-sm text-left">
              <HomeIcon className="w-5 h-5 text-[#e870b6] mt-1 flex-shrink-0" />
              <div className="text-sm sm:text-base text-[#3b1d76]/90 leading-relaxed">
                <p className="font-semibold text-[#1a0b3d]">Ichi &amp; Ori Pet Care</p>
                <p>G11, Tower C, Noida World One, Sector 90, Noida, Uttar Pradesh 201305</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8.5 — WHEN SHOULD YOU VISIT A VETERINARIAN? */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">KNOW WHEN TO CALL</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">When should you visit a veterinarian?</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">Early veterinary care often prevents small health concerns from turning into serious illnesses. The signs below are common reasons pet parents call us, and each one is worth a proper look sooner rather than later.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[
              'Loss of appetite',
              'Vomiting',
              'Diarrhea',
              'Persistent scratching or skin irritation',
              'Ear discharge or frequent head shaking',
              'Limping or difficulty walking',
              'Difficulty breathing',
              'Excessive thirst or urination',
              'Sudden weight loss',
              'Eye redness or discharge',
              'Unusual behaviour or lethargy',
              'Puppies or kittens missing vaccinations',
            ].map((sign, i) => (
              <div key={i} className="flex items-start gap-3 bg-[#fdf7fb] rounded-2xl px-5 py-4 border border-[#f3d6f5]/60">
                <span className="w-7 h-7 rounded-full bg-white border border-[#f5d3e6] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <ArrowRight className="w-3.5 h-3.5 text-[#e870b6]" />
                </span>
                <span className="text-[#1a0b3d] text-[15px] font-semibold leading-snug">{sign}</span>
              </div>
            ))}
          </div>

          <p className="text-center text-[#3b1d76]/85 text-base sm:text-[17px] leading-[1.85] max-w-2xl mx-auto mt-12">
            Please do not wait until symptoms worsen. If you are ever unsure, reach out. A quick call or WhatsApp message often helps us decide together whether a visit is needed today, tomorrow, or not at all.
          </p>
        </div>
      </section>

      {/* 9 — FAQ */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[880px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center mb-12">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">FREQUENTLY ASKED</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">Questions from Noida pet parents, answered.</h2>
          </div>
          <Accordion type="single" collapsible defaultValue="faq-0" className="divide-y divide-[#f5d3e6] border-y border-[#f5d3e6]" data-testid="vet-clinic-noida-faq">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-1">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left text-[#1a0b3d] font-semibold text-base sm:text-lg hover:no-underline py-5 sm:py-6">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#3b1d76]/85 text-sm sm:text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 10 — INTERNAL LINKS */}
      <section className="bg-[#fdf7fb] py-16 sm:py-20 border-y border-[#f3d6f5]/60">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 lg:px-10 text-center">
          <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">EXPLORE RELATED CARE</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1a0b3d] leading-[1.1] mb-8">Related veterinary services in Noida.</h2>
          <div className="flex flex-wrap justify-center gap-3">
            {INTERNAL_LINKS.map((l, i) => (
              <Link key={i} to={l.to} className="group inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-[#f3d6f5] hover:border-[#e870b6] hover:shadow-md text-[#1a0b3d] text-sm font-semibold transition-all">
                {l.label} <ArrowRight className="w-4 h-4 text-[#e870b6] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 10.5 — WHAT MAKES ICHI & ORI DIFFERENT */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-[11px] font-bold tracking-[0.28em] text-[#5b3eb5] mb-4">THE ICHI &amp; ORI DIFFERENCE</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1a0b3d] leading-[1.08]">What makes Ichi &amp; Ori different.</h2>
            <p className="text-[#3b1d76]/80 text-base sm:text-lg leading-relaxed mt-5">We believe great veterinary care is about more than diagnosing illness. It&apos;s about building trust, communicating honestly and treating every pet with the same compassion we&apos;d want for our own.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Users,          title: 'Treat Every Pet Like Family',   text: 'Every recommendation we make is the same one we’d make if your pet were our own.' },
              { Icon: ClipboardCheck, title: 'Honest, Transparent Care',      text: 'We explain every diagnosis, treatment option and expected cost before moving forward.' },
              { Icon: Microscope,     title: 'Evidence-Based Decisions',      text: 'Modern diagnostics and careful clinical judgement help us recommend the right treatment, not unnecessary procedures.' },
              { Icon: HeartPulse,     title: 'Long-Term Relationships',       text: 'Whether it’s a puppy’s first vaccination or care for a senior pet, we’re here to support your family through every stage of your pet’s life.' },
            ].map((p, i) => (
              <div key={i} className="bg-white rounded-2xl p-7 border border-[#f3d6f5]/60 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col h-full">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#fdf3fc] to-[#f3d6f5] flex items-center justify-center mb-5 flex-shrink-0">
                  <p.Icon className="w-5 h-5 text-[#5b3eb5]" />
                </div>
                <h3 className="font-serif text-[20px] font-bold text-[#1a0b3d] mb-3 leading-snug">{p.title}</h3>
                <p className="text-slate-600 text-[15px] leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 11 — FINAL CTA */}
      <section className="bg-white py-20 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 lg:px-10">
          <div className="rounded-[32px] bg-gradient-to-br from-[#5b3eb5] to-[#1a0b3d] text-white py-16 sm:py-20 px-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-25 [background-image:radial-gradient(circle_at_20%_30%,#e870b6_0%,transparent_45%)]" />
            <div className="relative text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 leading-[1.08]">Your Pet Deserves Exceptional Care.</h2>
              <p className="text-white/85 mb-10 max-w-xl mx-auto text-base sm:text-lg leading-relaxed">Whether it is your pet&rsquo;s first checkup, a worrying symptom or a scheduled surgery, our team is here to help. Book an appointment today and give your pet the calm, thoughtful care they deserve.</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4">
                <Link to="/book" onClick={() => trackBookButtonClick('vet_clinic_noida_final')} className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-[#e870b6] hover:bg-[#d85ba0] rounded-full px-8 py-6 font-semibold shadow-xl"><CalendarDays className="w-5 h-5 mr-2" />Request Appointment</Button>
                </Link>
                <a href={wa} target="_blank" rel="noreferrer" onClick={() => trackWhatsAppClick('vet_clinic_noida_final')} className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto bg-[#25d366] hover:bg-[#1eb858] rounded-full px-8 py-6 font-semibold shadow-xl"><MessageCircle className="w-5 h-5 mr-2" />WhatsApp</Button>
                </a>
                <a href={`tel:${CONTACT.phoneRaw}`} onClick={() => trackCallClick('vet_clinic_noida_final')} className="w-full sm:w-auto sm:ml-1">
                  <span className="inline-flex items-center gap-2 text-white/85 hover:text-white font-medium text-[14px] sm:text-[15px] underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors px-1 py-2">
                    <Phone className="w-4 h-4" /> Call Now
                  </span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
      <StickyMobileCta source="veterinaryclinic" />
    </div>
  );
};

export default VeterinaryClinicNoida;
